import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { NMAxiosInst, getDepartments } from "@neumetric/blueprint-lib/store";
import {
  NMStatsTable,
  BarChart,
  Col,
  Row,
  Input,
  NMStatsCards,
  DonutChart,
} from "@neumetric/blueprint-lib/components";
import { ExportSection } from "../ExportSection";

const safeNum = (v) => (v == null || v === "-" ? 0 : Number(v));

const MODULE_KEY = "Auditor_overview";

const Columns = [
  {
    title: "Department",
    attribute: "department",
    width: "25%",
    cellClassName: "ellipsis-text",
  },
  {
    title: "OVERALL",
    attribute: "total",
    width: "15%",
    overallFlag: true,
    highlight: { header: true, cell: true },
    align: "center-right",
  },
  {
    title: "Compliant",
    attribute: "compliant",
    id: "CLOSED",
    align: "center",
    width: "15%",
  },
  { title: "Gap", attribute: "gap", id: "GAP", align: "center", width: "15%" },
  {
    title: "Exempted",
    attribute: "exempted",
    id: "SKIPPED",
    align: "center",
    width: "15%",
  },
  {
    title: "Pending",
    attribute: "pending",
    id: "INVALID",
    align: "center",
    width: "15%",
  },
];

const PRIORITY_ORDER = [
  "Emergency",
  "Urgent",
  "Critical",
  "High",
  "Medium",
  "Low",
  "Minimal",
  "Least",
];

const PriorityColumns = [
  {
    title: "Priority",
    attribute: "priority",
    width: "25%",
    cellClassName: "ellipsis-text",
  },
  {
    title: "OVERALL",
    attribute: "total",
    overallFlag: true,
    highlight: { header: true, cell: true },
    align: "center-right",
  },
  { title: "Compliant", attribute: "compliant", id: "CLOSED", align: "center" },
  { title: "Gap", attribute: "gap", id: "GAP", align: "center" },
  { title: "Exempted", attribute: "exempted", id: "SKIPPED", align: "center" },
  { title: "Pending", attribute: "pending", id: "INVALID", align: "center" },
];

// turn 0 or "" into "-" for tables
const dashify = (v) =>
  v === 0 || v === "0" || v === "" || v == null ? "-" : v;

export function AuditWise(props) {
  const { user, audits, setSelectedAudit, mode, exportPayload } = props;

  const leafIdToTitle = (leafId) =>
    leafId && leafId.endsWith("_overview")
      ? leafId.slice(0, -"_overview".length)
      : leafId;

  // which audits should be exported off-screen?
  const auditsToExport = React.useMemo(() => {
    if (!exportPayload) return [];
    if (exportPayload.mode === "selected") {
      const titles = new Set(
        (exportPayload.selectedLeafIds || [])
          .filter((id) => typeof id === "string" && id.endsWith("_overview"))
          .map(leafIdToTitle)
      );
      return audits.filter((a) => titles.has(a.title));
    }
    if (exportPayload.mode === "first6" || mode === "export_everything") {
      return audits.slice(0, 6);
    }
    return [];
  }, [exportPayload, audits, mode]);

  const departments = useSelector(getDepartments);

  const [selectedIdx, setSelectedIdx] = useState(0);
  const selectedAudit =
    Array.isArray(audits) && audits.length > 0 ? audits[selectedIdx] : null;

  const [deptWiseRows, setDeptWiseRows] = useState(null);
  const [priorityRows, setPriorityRows] = useState(null);

  const isEmptyArr = (a) => !Array.isArray(a) || a.length === 0;
  const fetchAsmtStats = useCallback(async () => {
    if (!selectedAudit?.id) return;

    const payload = { asmt_id: selectedAudit.id };
    const res = await NMAxiosInst.post("/centrals/audit_stats", payload);

    const brandname = user.org.brandname;

    const depList = res?.department_stats ?? [];
    if (isEmptyArr(depList)) {
      setDeptWiseRows([]);
    } else {
      const depByLabel = new Map(
        depList.map((d) => [String(d.department).toLowerCase(), d])
      );
      const overallDept = {
        department: brandname,
        total: 0,
        compliant: 0,
        gap: 0,
        exempted: 0,
        pending: 0,
        highlight: true,
      };
      const deptRows = departments.map((dept) => {
        const s = depByLabel.get(String(dept.label).toLowerCase());
        const row = {
          department: dept.label,
          total: s ? dashify(s.total) : "-",
          compliant: s ? dashify(s.compliant) : "-",
          gap: s ? dashify(s.gap) : "-",
          exempted: s ? dashify(s.exempted) : "-",
          pending: s ? dashify(s.pending) : "-",
        };
        overallDept.total += safeNum(row.total);
        overallDept.compliant += safeNum(row.compliant);
        overallDept.gap += safeNum(row.gap);
        overallDept.exempted += safeNum(row.exempted);
        overallDept.pending += safeNum(row.pending);
        return row;
      });
      setDeptWiseRows([overallDept, ...deptRows]);
    }

    const priList = res?.priority_stats ?? [];
    if (isEmptyArr(priList)) {
      setPriorityRows([]);
    } else {
      const priByLabel = new Map(priList.map((p) => [String(p.priority), p]));
      const overallPri = {
        priority: brandname,
        total: 0,
        compliant: 0,
        gap: 0,
        exempted: 0,
        pending: 0,
        highlight: true,
      };
      const priRows = PRIORITY_ORDER.map((p) => {
        const s = priByLabel.get(p);
        const row = {
          priority: { id: p.toLowerCase(), label: p },
          total: s ? dashify(s.total) : "-",
          compliant: s ? dashify(s.compliant) : "-",
          gap: s ? dashify(s.gap) : "-",
          exempted: s ? dashify(s.exempted) : "-",
          pending: s ? dashify(s.pending) : "-",
        };
        overallPri.total += safeNum(row.total);
        overallPri.compliant += safeNum(row.compliant);
        overallPri.gap += safeNum(row.gap);
        overallPri.exempted += safeNum(row.exempted);
        overallPri.pending += safeNum(row.pending);
        return row;
      });
      setPriorityRows([overallPri, ...priRows]);
    }
  }, [selectedAudit, departments, user.org.brandname]);

  useEffect(() => {
    if (!selectedAudit || !(Array.isArray(audits) && audits.length > 0)) return;
    fetchAsmtStats();
  }, [selectedIdx, audits]);

  useEffect(() => {
    setSelectedAudit?.(selectedAudit?.title?.trim() || "");
  }, [selectedIdx, audits]);

  const deptWiseChartData = useMemo(() => {
    const chartData = {};
    departments.forEach((dept) => {
      const loading = deptWiseRows === null;
      const deptStat = !loading
        ? deptWiseRows.find((row) => row.department === dept.label)
        : null;

      chartData[dept.label] = [
        {
          key: "NONE",
          label: "Pending",
          value: loading ? null : safeNum(deptStat?.pending),
        },
        {
          key: "EXEMPTED",
          label: "Exempted",
          value: loading ? null : safeNum(deptStat?.exempted),
        },
        {
          key: "URGENT",
          label: "Gap",
          value: loading ? null : safeNum(deptStat?.gap),
        },
        {
          key: "COMPLETE",
          label: "Compliant",
          value: loading ? null : safeNum(deptStat?.compliant),
        },
      ];
    });
    return chartData;
  }, [deptWiseRows, departments]);

  const donutDatasets = useMemo(() => {
    if (!selectedAudit) {
      return [
        { key: "COMPLETE", value: null, label: "Compliant" },
        { key: "PENDING", value: null, label: "Gap" },
        { key: "EXEMPTED", value: null, label: "Exempted" },
        { key: "INVALID", value: null, label: "Pending" },
      ];
    }
    return [
      {
        key: "COMPLETE",
        value: safeNum(selectedAudit.compliant),
        label: "Compliant",
      },
      { key: "PENDING", value: safeNum(selectedAudit.gap), label: "Gap" },
      {
        key: "EXEMPTED",
        value: safeNum(selectedAudit.exemption),
        label: "Exempted",
        border: "true",
      },
      {
        key: "INVALID",
        value: safeNum(selectedAudit.pending),
        label: "Pending",
        border: "true",
      },
    ];
  }, [selectedAudit]);

  const totalControls = selectedAudit ? safeNum(selectedAudit.controls) : 0;

  const auditId = selectedAudit?.title?.trim() || "audit";

  const priorityChartDatasets = useMemo(() => {
    const n = (v) => (v === "-" || v == null ? 0 : Number(v));
    const brand = user?.org?.brandname;

    const rows = (priorityRows || []).filter(
      (r) => r?.priority !== brand && typeof r?.priority !== "string"
    );

    const SERIES_ORDER = [
      { key: "INVALID", label: "Pending", field: "pending" },
      { key: "EXEMPTED", label: "Exempted", field: "exempted" },
      { key: "PENDING", label: "Gap", field: "gap" },
      { key: "COMPLETE", label: "Compliant", field: "compliant" },
    ];

    const axis = PRIORITY_ORDER.slice();

    const byLabel = new Map(
      rows.map((r) => [
        typeof r.priority === "object" ? r.priority.label : r.priority,
        r,
      ])
    );

    return Object.fromEntries(
      axis.map((label) => {
        const r = byLabel.get(label) || {};
        return [
          label,
          SERIES_ORDER.map((s) => ({
            key: s.key,
            label: s.label,
            value: n(r[s.field]),
          })),
        ];
      })
    );
  }, [priorityRows, user]);

  const NUMERIC_KEYS = ["total", "compliant", "gap", "exempted", "pending"];

  const isAllDashRow = (row) =>
    NUMERIC_KEYS.every((k) => row?.[k] === "-" || row?.[k] == null);

  const filterRowsForTable = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    const [head, ...rest] = rows;
    const keepHead = head?.highlight === true;
    const filtered = rest.filter((r) => !isAllDashRow(r));
    return keepHead ? [head, ...filtered] : filtered;
  };

  const filteredDeptWiseRows = useMemo(
    () => filterRowsForTable(deptWiseRows),
    [deptWiseRows]
  );
  const filteredPriorityRows = useMemo(
    () => filterRowsForTable(priorityRows),
    [priorityRows]
  );
  //adding for exporting list of audits-

  // build rows from API response (reused for selected & export-only)
  const rowsFromResponse = React.useCallback(
    (res) => {
      const brandname = user.org.brandname;

      // Department-wise
      const depList = res?.department_stats ?? [];
      let _deptRows = [];
      if (Array.isArray(depList) && depList.length) {
        const depByLabel = new Map(
          depList.map((d) => [String(d.department).toLowerCase(), d])
        );
        const overallDept = {
          department: brandname,
          total: 0,
          compliant: 0,
          gap: 0,
          exempted: 0,
          pending: 0,
          highlight: true,
        };
        const rows = departments.map((dept) => {
          const s = depByLabel.get(String(dept.label).toLowerCase());
          const row = {
            department: dept.label,
            total: s ? dashify(s.total) : "-",
            compliant: s ? dashify(s.compliant) : "-",
            gap: s ? dashify(s.gap) : "-",
            exempted: s ? dashify(s.exempted) : "-",
            pending: s ? dashify(s.pending) : "-",
          };
          overallDept.total += safeNum(row.total);
          overallDept.compliant += safeNum(row.compliant);
          overallDept.gap += safeNum(row.gap);
          overallDept.exempted += safeNum(row.exempted);
          overallDept.pending += safeNum(row.pending);
          return row;
        });
        _deptRows = [overallDept, ...rows];
      } else {
        _deptRows = [];
      }

      // Priority-wise
      const priList = res?.priority_stats ?? [];
      let _priRows = [];
      if (Array.isArray(priList) && priList.length) {
        const priByLabel = new Map(priList.map((p) => [String(p.priority), p]));
        const overallPri = {
          priority: brandname,
          total: 0,
          compliant: 0,
          gap: 0,
          exempted: 0,
          pending: 0,
          highlight: true,
        };
        const rows = PRIORITY_ORDER.map((p) => {
          const s = priByLabel.get(p);
          const row = {
            priority: { id: p.toLowerCase(), label: p },
            total: s ? dashify(s.total) : "-",
            compliant: s ? dashify(s.compliant) : "-",
            gap: s ? dashify(s.gap) : "-",
            exempted: s ? dashify(s.exempted) : "-",
            pending: s ? dashify(s.pending) : "-",
          };
          overallPri.total += safeNum(row.total);
          overallPri.compliant += safeNum(row.compliant);
          overallPri.gap += safeNum(row.gap);
          overallPri.exempted += safeNum(row.exempted);
          overallPri.pending += safeNum(row.pending);
          return row;
        });
        _priRows = [overallPri, ...rows];
      } else {
        _priRows = [];
      }

      return { deptWiseRows: _deptRows, priorityRows: _priRows };
    },
    [departments, user.org.brandname]
  );
  // cache for export-only rows by audit id
  const exportCacheRef = React.useRef({});
  const [, force] = React.useState(0);
  const bump = () => force((t) => t + 1);

  // prefetch stats for audits to export
  React.useEffect(() => {
    let cancelled = false;
    async function run() {
      for (const a of auditsToExport) {
        const id = a?.id;
        if (!id || exportCacheRef.current[id]) continue;
        const res = await NMAxiosInst.post("/centrals/audit_stats", {
          asmt_id: id,
        });
        if (cancelled) return;
        exportCacheRef.current[id] = rowsFromResponse(res);
        bump(); // re-render hidden bundles once this audit's rows are ready
      }
    }
    if (auditsToExport.length) run();
    return () => {
      cancelled = true;
    };
  }, [auditsToExport, rowsFromResponse]);

  const buildDeptChartFromRows = React.useCallback(
    (rows) => {
      const n = (v) => (v === "-" || v == null ? 0 : Number(v));
      const chartData = {};
      departments.forEach((dept) => {
        const r = (rows || []).find((row) => row.department === dept.label);
        chartData[dept.label] = [
          { key: "NONE", label: "Pending", value: n(r?.pending) },
          { key: "EXEMPTED", label: "Exempted", value: n(r?.exempted) },
          { key: "URGENT", label: "Gap", value: n(r?.gap) },
          { key: "COMPLETE", label: "Compliant", value: n(r?.compliant) },
        ];
      });
      return chartData;
    },
    [departments]
  );

  const buildPriorityChartFromRows = React.useCallback(
    (rows) => {
      const n = (v) => (v === "-" || v == null ? 0 : Number(v));
      const brand = user?.org?.brandname;
      const bodyRows = (rows || []).filter(
        (r) => r?.priority !== brand && typeof r?.priority !== "string"
      );
      const SERIES_ORDER = [
        { key: "INVALID", label: "Pending", field: "pending" },
        { key: "EXEMPTED", label: "Exempted", field: "exempted" },
        { key: "PENDING", label: "Gap", field: "gap" },
        { key: "COMPLETE", label: "Compliant", field: "compliant" },
      ];
      const byLabel = new Map(
        bodyRows.map((r) => [
          typeof r.priority === "object" ? r.priority.label : r.priority,
          r,
        ])
      );
      return Object.fromEntries(
        PRIORITY_ORDER.map((label) => {
          const r = byLabel.get(label) || {};
          return [
            label,
            SERIES_ORDER.map((s) => ({
              key: s.key,
              label: s.label,
              value: n(r[s.field]),
            })),
          ];
        })
      );
    },
    [user]
  );

  let indexForExport = 1;
  return Array.isArray(audits) && audits.length > 0 ? (
    <>
      {(mode === "full" || mode === "export_everything") && (
        <>
          <div style={{ padding: "0 40px 0 40px" }}>
            <Col style={{ maxWidth: 360 }}>
              <Input
                typeRepresentation="DROPDOWN"
                name="audit"
                label="Audit"
                labelAlign="middle"
                showLabelColon={false}
                value={String(selectedIdx)}
                options={audits.map((a, idx) => ({
                  id: String(idx),
                  label: a.title,
                  key: a.id || a.title,
                }))}
                onChange={(attrOrVal, maybeVal) => {
                  const v =
                    typeof maybeVal === "undefined" ? attrOrVal : maybeVal;
                  const id =
                    typeof v === "object" && v !== null ? v.id ?? v.value : v;
                  const nextIdx = Number(id);
                  setSelectedIdx(nextIdx);
                  const nextAudit = audits[nextIdx];
                  setSelectedAudit?.(nextAudit?.title?.trim() || "");
                }}
                classes={{ root: "w-100" }}
              />
            </Col>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 20,
                padding: "0",
                flexWrap: "wrap",
              }}
            >
              {selectedAudit && (
                <>
                  <Col style={{}}>
                    <NMStatsCards
                      cards={[
                        {
                          id: `${auditId}-pending`,
                          key: "pending",
                          label: "Pending",
                          value: safeNum(selectedAudit.pending),
                          classes: { root: "bg-invalid" },
                        },
                        {
                          id: `${auditId}-compliant`,
                          key: "compliant",
                          label: "Compliant",
                          value: safeNum(selectedAudit.compliant),
                          classes: { root: "bg-approved" },
                        },
                        {
                          id: `${auditId}-gap`,
                          key: "gap",
                          label: "Gap",
                          value: safeNum(selectedAudit.gap),
                          classes: { root: "bg-pending" },
                        },
                        {
                          id: `${auditId}-exempted`,
                          key: "exempted",
                          label: "Exempted",
                          value: safeNum(selectedAudit.exemption),
                          classes: {
                            root: "bg-exempted",
                            label: "text-contrast",
                            value: "text-contrast",
                          },
                        },
                      ]}
                      classes={{
                        root: "doc-cards",
                        card: "pt-10 pb-10 pl-8 pr-8 w-90-px",
                        value: "fs-32 text-right w-100",
                        label: "fs-16",
                      }}
                    />
                  </Col>

                  <Col style={{ flex: "1 1 0", minWidth: 350 }}>
                    <DonutChart
                      chartId="auditor-selected-audit"
                      title={selectedAudit.title || ""}
                      subtitle="Controls:"
                      totalCount={String(totalControls || "-")}
                      showDateInHeader={true}
                      classes={{ root: "h-200-px" }}
                      datasets={donutDatasets}
                      metric={{
                        label: "Score",
                        value: selectedAudit.score,
                        type: "score",
                      }}
                    />
                  </Col>
                </>
              )}
            </div>
            <Row style={{ padding: "0 ", gap: 16 }}>
              <Col className="w-44">
                <div
                  style={{
                    flex: "1 1 48%",
                    minWidth: 400,
                    maxWidth: "100%",
                    paddingTop: "20px",
                  }}
                >
                  <NMStatsTable
                    title="Audit"
                    subTitle="Department-wise"
                    columns={Columns}
                    dataSource={filteredDeptWiseRows}
                    showDateInHeader={true}
                    getCellTypeRepresentation={(cellInfo) => {
                      if (
                        cellInfo.rowId === -1 &&
                        ["pending", "compliant", "gap", "exempted"].includes(
                          cellInfo.columnId
                        )
                      ) {
                        return "DOT";
                      }
                      return null;
                    }}
                  />
                </div>
              </Col>

              <Col className="w-55">
                <div
                  className="w-100"
                  style={{ paddingBottom: 28, paddingTop: 32 }}
                >
                  <BarChart
                    title="Audit"
                    subtitle="Department-wise"
                    label={{ yAxis: "Department", xAxis: "Controls" }}
                    datasets={deptWiseChartData}
                    stackType="normal"
                    classes={{ root: "h-680-px w-100" }}
                    showDateInHeader={true}
                  />
                </div>
              </Col>
            </Row>

            <Row style={{ padding: "0 ", gap: 16, marginTop: 8 }}>
              <Col className="w-44">
                <div
                  style={{
                    flex: "1 1 48%",
                    minWidth: 400,
                    maxWidth: "100%",
                  }}
                >
                  <NMStatsTable
                    title="Audit"
                    subTitle="Priority-wise"
                    columns={PriorityColumns}
                    dataSource={filteredPriorityRows}
                    showDateInHeader={true}
                    getCellTypeRepresentation={(cellInfo) => {
                      if (
                        cellInfo.rowId === -1 &&
                        ["compliant", "gap", "exempted", "pending"].includes(
                          cellInfo.columnId
                        )
                      ) {
                        return "DOT";
                      }
                      if (
                        cellInfo.columnId === "priority" &&
                        cellInfo.rowId > 0
                      )
                        return "DOT";
                      return null;
                    }}
                  />
                </div>
              </Col>

              <Col className="w-55">
                <div
                  className="w-100"
                  style={{ paddingTop: 12, paddingBottom: 28 }}
                >
                  <BarChart
                    key={`priority-${audits[selectedIdx]?.key || selectedIdx}`}
                    showDateInHeader={true}
                    title="Audit"
                    subtitle="Priority-wise"
                    label={{ yAxis: "Priority", xAxis: "Controls" }}
                    datasets={priorityChartDatasets}
                    stackType="normal"
                    classes={{ root: "h-500-px w-100" }}
                  />
                </div>
              </Col>
            </Row>
          </div>

          {(auditsToExport.length > 0 ||
            mode === "export_everything" ||
            mode === "full") && (
            <div style={{ display: "block" }}>
              {auditsToExport.map((a, k) => {
                const base = 100 + k * 10; // avoid collisions with visible 1..5
                const totalControls = safeNum(a.controls);

                const donutDatasetsForA = [
                  {
                    key: "COMPLETE",
                    value: safeNum(a.compliant),
                    label: "Compliant",
                  },
                  { key: "PENDING", value: safeNum(a.gap), label: "Gap" },
                  {
                    key: "EXEMPTED",
                    value: safeNum(a.exemption),
                    label: "Exempted",
                    border: true,
                  },
                  {
                    key: "INVALID",
                    value: safeNum(a.pending),
                    label: "Pending",
                    border: true,
                  },
                ];

                const cached = exportCacheRef.current[a.id] || {};
                const _deptRows = cached.deptWiseRows || [];
                const _priRows = cached.priorityRows || [];

                return (
                  <React.Fragment key={`audit-export-${a.id || k}`}>
                    {/* 1)  Donut */}
                    <ExportSection
                      moduleKey={MODULE_KEY}
                      index={indexForExport++}
                      style={{ display: "inline-block", width: 350 }}
                    >
                      <Col style={{ flex: "1 1 0", minWidth: 350 }}>
                        <div style={{ width: "100%", height: 200 }}>
                          <DonutChart
                            chartId={`auditor-${a.id}`}
                            title={a.title || ""}
                            subtitle="Controls:"
                            totalCount={String(totalControls || "-")}
                            showDateInHeader={true}
                            classes={{ root: "h-200-px" }}
                            datasets={donutDatasetsForA}
                            metric={{
                              label: "Score",
                              value: a.score,
                              type: "score",
                            }}
                          />
                        </div>
                      </Col>
                    </ExportSection>

                    {/* 2) Dept-wise Table */}
                    <ExportSection
                      moduleKey={MODULE_KEY}
                      index={indexForExport++}
                      exportType="table"
                      exportData={{
                        title: "Audit - Department-wise",
                        columns: Columns,
                        rows: _deptRows,
                      }}
                      style={{ paddingTop: 20 }}
                    >
                      <NMStatsTable
                        title="Audit"
                        subTitle="Department-wise"
                        columns={Columns}
                        dataSource={_deptRows}
                        showDateInHeader={true}
                        getCellTypeRepresentation={(cellInfo) => {
                          if (
                            cellInfo.rowId === -1 &&
                            [
                              "pending",
                              "compliant",
                              "gap",
                              "exempted",
                            ].includes(cellInfo.columnId)
                          )
                            return "DOT";
                          return null;
                        }}
                      />
                    </ExportSection>

                    {/* 3) Dept-wise Chart */}
                    <ExportSection
                      moduleKey={MODULE_KEY}
                      index={indexForExport++}
                      exportType="chart"
                      className="w-100"
                      style={{ paddingBottom: 28, paddingTop: 32 }}
                    >
                      <BarChart
                        title="Audit"
                        subtitle="Department-wise"
                        label={{ yAxis: "Department", xAxis: "Controls" }}
                        datasets={buildDeptChartFromRows(_deptRows)}
                        stackType="normal"
                        classes={{ root: "h-680-px w-100" }}
                        showDateInHeader={true}
                      />
                    </ExportSection>

                    {/* 4) Priority-wise Table */}
                    <ExportSection
                      moduleKey={MODULE_KEY}
                      index={indexForExport++}
                      exportType="table"
                      exportData={{
                        title: "Audit - Priority-wise",
                        columns: PriorityColumns,
                        rows: _priRows,
                      }}
                    >
                      <NMStatsTable
                        title="Audit"
                        subTitle="Priority-wise"
                        columns={PriorityColumns}
                        dataSource={_priRows}
                        showDateInHeader={true}
                        getCellTypeRepresentation={(cellInfo) => {
                          if (
                            cellInfo.rowId === -1 &&
                            [
                              "compliant",
                              "gap",
                              "exempted",
                              "pending",
                            ].includes(cellInfo.columnId)
                          )
                            return "DOT";
                          if (
                            cellInfo.columnId === "priority" &&
                            cellInfo.rowId > 0
                          )
                            return "DOT";
                          return null;
                        }}
                      />
                    </ExportSection>

                    {/* 5) Priority-wise Chart */}
                    <ExportSection
                      moduleKey={MODULE_KEY}
                      index={indexForExport++}
                      exportType="chart"
                      className="w-100"
                      style={{ paddingTop: 12, paddingBottom: 28 }}
                    >
                      <BarChart
                        key={`priority-${a.key || a.id || k}`}
                        showDateInHeader={true}
                        title="Audit"
                        subtitle="Priority-wise"
                        label={{ yAxis: "Priority", xAxis: "Controls" }}
                        datasets={buildPriorityChartFromRows(_priRows)}
                        stackType="normal"
                        classes={{ root: "h-500-px w-100" }}
                      />
                    </ExportSection>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </>
      )}
      {mode === "fusion" && (
        <>
          {audits.slice(0, 6).map((d, i) => {
            const totalControls = safeNum(d.controls);
            const datasets = [
              {
                key: "COMPLETE",
                value: safeNum(d.compliant),
                label: "Compliant",
                sortOrder: 3,
                legendOrder: 1,
                textColor: "#FFF",
              },
              {
                key: "PENDING",
                value: safeNum(d.gap),
                label: "Gap",
                sortOrder: 2,
                legendOrder: 2,
                textColor: "#FFF",
              },
              {
                key: "EXEMPTED",
                value: safeNum(d.exemption),
                label: "Exempted",
                border: true,
                sortOrder: 1,
                legendOrder: 3,
              },
              {
                key: "INVALID",
                value: safeNum(d.pending),
                label: "Pending",
                border: true,
                sortOrder: 4,
                legendOrder: 4,
              },
            ];
            // build one-row table version of this donut data
            const fusionRow = {
              id: d.id,
              title: d.title,
              controls: d.controls,
              compliant: d.compliant,
              gap: d.gap,
              exemption: d.exemption,
              pending: d.pending,
              score: d.score,
            };

            const fusionColumns = [
              { title: "ID", attribute: "id" },
              { title: "Title", attribute: "title" },
              { title: "Controls", attribute: "controls" },
              { title: "Compliant", attribute: "compliant" },
              { title: "Gap", attribute: "gap" },
              { title: "Exemption", attribute: "exemption" },
              { title: "Pending", attribute: "pending" },
              { title: "Score", attribute: "score" },
            ];

            return (
              <div
                style={{ padding: "20px 0 0 10px" }}
                key={`fusion-audit-${d.id || i}`}
              >
                <ExportSection
                  moduleKey={MODULE_KEY}
                  index={i + 1}
                  // key={d.id || i}
                  exportType={mode === "export_everything" ? "chart" : "table"}
                  exportData={{
                    title: `Audit Summary - ${d.title}`,
                    columns: fusionColumns,
                    rows: [fusionRow],
                  }}
                  style={{
                    display: "inline-block",
                    width: 350,
                    flex: "0 0 auto",
                  }}
                >
                  <Col style={{ flex: "1 1 0", minWidth: 350 }}>
                    <div style={{ width: "100%", height: 200 }}>
                      <DonutChart
                        chartId={`audit-${i}`}
                        title={d.title}
                        subtitle="Controls:"
                        totalCount={String(totalControls || "-")}
                        classes={{ root: "h-200-px" }}
                        datasets={datasets}
                        metric={{
                          label: "Score",
                          value: d.score,
                          type: "score",
                        }}
                        style={{ width: "100%" }}
                      />
                    </div>
                  </Col>
                </ExportSection>
              </div>
            );
          })}
        </>
      )}
    </>
  ) : null;
}
